import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { SelectedProjectApiService } from './selected-project.api.service';
import { Project } from '../../models/project';
import { exhaustMap, filter, firstValueFrom, from, take } from 'rxjs';
import { OrderByPipe } from '../../pipes/order-by.pipe';
import { Environmental_Assessments, UserLogicService } from '../../services/user.logic.service';
import { ModelLibraryApiService } from '../../services/model-library.api.service';
import { ModelLibrary } from '../../models/model-library';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormulationApiService } from '../../services/echo/formulation.api.service';
import { Formulation } from '../../models/formulation';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { EEAGapLogicService } from 'src/app/environmental-exposure-assessment/eea-gap/eea-gap.logic.service';
import { Catalog } from '../../models/echo/catalog';
import { Constants } from '../../utils/constants';
import { enrichProjectXCompoundXModels } from '../../utils/project-x-compound-x-model.utils';
import { ProjectDeepLinkService } from '../../services/project-deep-link.service';

@Component({
  selector: 'app-selected-project',
  templateUrl: './selected-project.component.html',
  styleUrls: ['./selected-project.component.css']
})
export class SelectedProjectComponent implements OnInit {

  @Input() isEEA:boolean = false;
  @Input() isERA:boolean = false;
  @Input() isHHRA:boolean = false;
  @Input() isESAUDL:boolean = false;
  @Input() isEF:boolean = false;
  @Input() featureAcronym?: Environmental_Assessments;
  destroyRef = inject(DestroyRef)

  advancedFiltersVisibles: boolean = false;
  projects: Project[] = [];
  searchFilteredProjects: Project[] = [];
  searchUdlFilteredProjects: Project[] = [];
  models: ModelLibrary[] = [];
  selectedProject?: Project;
  createdBy: string = '';
  geographies: Catalog[] = [];
  currentUserCanRunModels: boolean = true;
  constructor(public selectedProjectApiService: SelectedProjectApiService,
    public userLogicService: UserLogicService,
    private modelLibraryApiService: ModelLibraryApiService,
    private formulationAPIService: FormulationApiService,
    private deepLinkService: ProjectDeepLinkService,
    private gapLogicSWService: EEAGapLogicService) {
  }

  async ngOnInit(): Promise<void> {
    this.selectedProjectApiService.featureAcronym = this.featureAcronym;
    this.currentUserCanRunModels = await this.userLogicService.currentUserCanRunModelsAsync(this.featureAcronym);
    this.getMatchingFACTSOPEX();
    this.createdBy = this.userLogicService.profile.displayName ?? "";

    this.selectedProjectApiService.projects
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projects: Project[]) => {
        this.projects = projects;
        this.selectedProjectApiService.loadingProjects = false;
      });

    this.selectedProjectApiService.selectedProject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((project?: Project) => {
        this.selectedProject = project;
        if(project) this.selectedProjectApiService.loadingProjects = false;
      });

    this.selectedProjectApiService.selectProjectRequest$
      .pipe(
        filter((projectPk: number) => projectPk !== this.selectedProject?.projectPk),
        exhaustMap((projectPk: number) => from(this.selectProjectByPk(projectPk))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    const deepLinkProjectPk = this.deepLinkService.projectPk;
    if (deepLinkProjectPk != undefined) {
      this.selectedProjectApiService.myProjectsFilter = false;
      await this.selectedProjectApiService.getProjects('NA');
    } else if (this.selectedProjectApiService.areProjectsEmpty()) {
      await this.selectedProjectApiService.getProjects(this.createdBy);
    }

    await this.getModels();
    await this.iniValues();

    if (this.isESAUDL && this.selectedProject?.source == null || !this.isESAUDL && this.selectedProject?.source?.toUpperCase() === Constants.FEATURE_ACRONYM.ESA_ASSESSMENT_UDL) {
      this.selectedProject = undefined; // when switching between UDL and non-UDL assessments, clear the selected project
      this.selectedProjectApiService.setSelectedProject(undefined);
    }

    if (deepLinkProjectPk != undefined) await this.selectProjectByPk(deepLinkProjectPk);
  }

  // when switching between components we need to reset the filters
  async onFocus($event: Event) {
    this.filterProjects();
  }

  filterProjects() {
    const filterOwnedProjects = (project: Project) => this.selectedProjectApiService.myProjectsFilter ? project.createdBy === this.createdBy : true;
    const filterAssessmentProjects = (project: Project) => this.isESAUDL ? project.source?.toUpperCase() === Constants.FEATURE_ACRONYM.ESA_ASSESSMENT_UDL : project.source?.toUpperCase() !== Constants.FEATURE_ACRONYM.ESA_ASSESSMENT_UDL;
    this.isESAUDL
      ? this.searchUdlFilteredProjects = this.projects.filter(project => filterAssessmentProjects(project) && filterOwnedProjects(project))
      : this.searchFilteredProjects = this.projects.filter(project => filterAssessmentProjects(project) && filterOwnedProjects(project));
  }

  getPlaceHolder(){
    return this.selectedProjectApiService.loadingProjects ? 'Loading...' : this.selectedProject !== undefined ? undefined : 'Select existing project';
  }

  iniValues() {
    let promises = [
      this.gapLogicSWService.getGeographies(),
    ]
    return Promise.all(promises)
      .then(async (results) => {
        this.geographies = results[0];
      })
  }

  initNewProject() {
    this.selectedProjectApiService.initNewProject(this.createdBy);
  }

  getMatchingFACTSOPEX() {
    this.selectedProjectApiService.getMatchingFACTSOPEX().pipe(take(1)).subscribe((data) => {
      this.selectedProjectApiService.matchingFACTSOPEX = data;
    });
  }

  queryFilterProjects({ query }: AutoCompleteCompleteEvent) {
    this.searchFilteredProjects = this.projects.filter((project: Project) => project.name?.toLowerCase().indexOf(query.toLowerCase()) == 0 && project.source?.toUpperCase() != Constants.FEATURE_ACRONYM.ESA_ASSESSMENT_UDL);
    this.searchUdlFilteredProjects = this.projects.filter((project: Project) => project.source?.toUpperCase() === Constants.FEATURE_ACRONYM.ESA_ASSESSMENT_UDL);
  }

  async onSelectionChanged(project: Project) {
    await this.selectProjectByPk(project.projectPk);
  }

  async selectProjectByPk(projectPk: number): Promise<void> {
    this.selectedProjectApiService.loadingProjects = true;
    this.selectedProjectApiService.setSelectedProject(undefined);
    const project = await firstValueFrom(this.selectedProjectApiService.getProjectByPk(projectPk));
    await this.setFormulationToSelectedProject(project);
    this.setGeographyToSelectedProject(project);
    this.setProjectXModels(project);
    this.selectedProjectApiService.setSelectedProject(project);
  }

  setGeographyToSelectedProject(project: Project) {
    const geographiesToFilter = [Constants.CROP_GEOGRAPHIES.EUROPE_UNION, Constants.CROP_GEOGRAPHIES.UK, Constants.CROP_GEOGRAPHIES.USA, Constants.CROP_GEOGRAPHIES.BRAZIL];
    project.geography = this.geographies.filter((x: Catalog) => geographiesToFilter.includes(x.name ?? ''))?.find((x: Catalog) => project.countryPk === x.key || project.regionPk === x.key)?.name;
    if (project?.geographyName && project.geography == null) {
      project.regionPk = project.regionPk ?? undefined;
      project.countryPk = project.countryPk ?? undefined;
      project.geography = this.geographies.find(x => x.name == project?.geographyName)?.name ?? "";
    }
  }

  async userFilterProjects(state: boolean): Promise<void> {
    this.selectedProjectApiService.setSelectedProject();
    this.searchFilteredProjects = [];
    this.searchUdlFilteredProjects = [];
    this.selectedProjectApiService.loadingProjects = true;
    this.selectedProjectApiService.myProjectsFilter = state ?? false;
    this.selectedProjectApiService.myProjectsFilter
      ? await this.selectedProjectApiService.getProjects(this.createdBy)
      : await this.selectedProjectApiService.getProjects('NA');
  }

  async getModels(): Promise<void> {
    this.models = await firstValueFrom(this.modelLibraryApiService.getModels().pipe(take(1)));
    this.setProjectsXModels();
    if (this.selectedProject) {
      this.setProjectXModels(this.selectedProject);
      this.selectedProjectApiService.setSelectedProject({ ...this.selectedProject });
    }
  }

  async setFormulationToSelectedProject(project: Project) {
    if(project.formulationPk){
      await firstValueFrom(this.formulationAPIService.getFormulationByPk(project.formulationPk).pipe(take(1))).then((formulation: Formulation) => {
        project.formulation = formulation.tradeName;
      }).catch(() =>{});
    }
  }

  private setProjectsXModels() {
    this.projects.forEach(project => {
      this.setProjectXModels(project);
    });
  }

  private setProjectXModels(project: Project): void {
    project.projectXCompoundXModel = enrichProjectXCompoundXModels(project.projectXCompoundXModel, this.models);
  }

  showAdvancedFiltersDialog() {
    this.advancedFiltersVisibles = !this.advancedFiltersVisibles
  }

  handleAdvancedFilterRow(project: any) {
    this.advancedFiltersVisibles = !this.advancedFiltersVisibles;
    this.onSelectionChanged(project)
  }
}


